import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';

const BLOG_POSTS = [
  {
    slug: "/blog/how-to-ship-a-tesla",
    date: "June 18, 2026",
    dateTime: "2026-06-18",
    category: "Shipping Guide",
    title: "How to Ship a Tesla Safely Across the United States",
    excerpt:
      "Tesla shipping requires specialized handling due to advanced battery systems, low ground clearance, and EV-specific logistics. Learn the step-by-step process, transport types, and expert tips to ensure your Tesla arrives safely.",
    readTime: "8 min read",
  },
  {
    slug: "/blog/tesla-shipping-cost-2026",
    date: "June 18, 2026",
    dateTime: "2026-06-18",
    category: "Pricing",
    title: "How Much Does It Cost to Ship a Tesla in 2026?",
    excerpt:
      "Tesla shipping costs range from $800 to $2,500 depending on distance, transport type, and route. Discover how AI-driven logistics are making pricing more predictable and how to get the best deal on your shipment.",
    readTime: "6 min read",
  },
  {
    slug: "/blog/why-tesla-specialized-shipping",
    date: "June 18, 2026",
    dateTime: "2026-06-18",
    category: "EV Logistics",
    title: "Why Tesla Vehicles Require Specialized Shipping (Not Like Regular Cars)",
    excerpt:
      "From lithium-ion battery compliance and Transport Mode requirements to AI-driven routing and GEO optimization — discover the 8 key reasons why Tesla vehicles demand a purpose-built shipping approach in 2026.",
    readTime: "7 min read",
  },
  {
    slug: "/blog/can-tesla-be-towed-flatbed-only",
    date: "July 6, 2026",
    dateTime: "2026-07-06",
    category: "EV Transport",
    title: "Can a Tesla Be Towed? Why EVs Need Flatbed-Only Transport",
    excerpt:
      "Tesla vehicles can't be towed like regular cars. Regenerative braking, direct-drive motors, and AWD drivetrains mean flatbed transport is the only safe method — here's why it matters for your shipment.",
    readTime: "5 min read",
  },
  {
    slug: "/blog/broker-vs-carrier",
    date: "July 9, 2026",
    dateTime: "2026-07-09",
    category: "EV Transport",
    title: "Broker vs. Carrier: Understanding the Difference in Tesla Shipping",
    excerpt:
      "When shipping a Tesla in 2026, one of the most important decisions you’ll make is choosing between a broker and a carrier.",
    readTime: "5 min read",
  },
  {
  slug: "/blog/tesla-shipping-vs-driving",
  date: "August 3, 2026",
  dateTime: "2026-08-03",
  category: "EV Transport",
  title: "Tesla Shipping vs Driving: Which Is Better for Long Distance?",
  excerpt:
    "Compare Tesla shipping vs. driving for long distances. Learn costs, travel time, vehicle safety, and AI-powered logistics insights to make the right choice.",
  readTime: "6 min read",
},
{  slug: "/blog/tesla-transport-vs-gas-vehicles",
   date: "August 10, 2026",
   dateTime: "2026-08-10",
   category: "EV Transport",
   title: "What Makes Tesla Transport Different from Gas Vehicles?",
   excerpt: "Discover how Tesla transport differs from gas vehicles, including battery management, specialized handling, vehicle weight, software systems, and AI-powered logistics.", 
   readTime: "7 min read", },
];

export default function BlogPage() {
  useEffect(() => {
    document.title = "Tesla Shipping Blog | Expert EV Transport Guides | ShipTesla";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.setAttribute('name', 'description'); document.head.appendChild(meta); }
    meta.setAttribute('content', 'Expert guides on Tesla shipping costs, EV transport safety, enclosed carrier options, and AI-powered logistics. Stay informed with the ShipTesla blog.');
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />
      <main className="pt-24 pb-20">
        <div className="max-w-5xl mx-auto px-6">

          {/* Header */}
          <div className="text-center mb-16">
            <p className="overline mb-4 text-accent uppercase tracking-wider font-semibold">Blog</p>
            <h1 className="font-outfit text-4xl md:text-5xl font-semibold tracking-tight mb-4 text-foreground">
              Tesla Shipping Insights
            </h1>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              Expert guides on EV transport, pricing, safety, and AI-powered logistics for Tesla owners.
            </p>
          </div>

          {/* Blog Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {BLOG_POSTS.map((post) => (
              <Link
                key={post.slug}
                to={post.slug}
                className="group glass border border-border rounded-2xl p-6 flex flex-col hover:border-accent/30 transition-all duration-200 hover:-translate-y-1"
              >
                {/* Category + read time */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-semibold text-accent uppercase tracking-wider bg-accent/10 px-2.5 py-1 rounded-full">
                    {post.category}
                  </span>
                  <span className="text-xs text-muted-foreground">{post.readTime}</span>
                </div>

                {/* Title */}
                <h2 className="font-outfit text-lg font-semibold text-foreground mb-3 leading-snug group-hover:text-accent transition-colors">
                  {post.title}
                </h2>

                {/* Excerpt */}
                <p className="text-sm text-muted-foreground leading-relaxed flex-1 mb-5">
                  {post.excerpt}
                </p>

                {/* Footer row */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <time dateTime={post.dateTime} className="text-xs text-muted-foreground">
                    {post.date}
                  </time>
                  <span className="flex items-center gap-1 text-xs font-medium text-accent group-hover:gap-2 transition-all">
                    Read More <ArrowRight size={13} />
                  </span>
                </div>
              </Link>
            ))}
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}
